/**
 * Authenticated dashboard: paginated list of worker monthly timesheet submissions.
 * Uses infinite scroll and debounced search (same patterns as OrdersPage).
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AppContext } from '../../App';
import InfiniteScroll from 'react-infinite-scroller';
import {
  Button,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
} from 'react-bootstrap';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import DropdownSelect from '../../components/Generic/DropdownSelect';
import WorkerTimesheetsTable from '../../components/WorkerTimesheet/WorkerTimesheetsTable';
import WorkerTimesheetMonthlyExportModal from '../../components/WorkerTimesheet/WorkerTimesheetMonthlyExportModal';
import {
  getWorkerTimesheets,
  getWorkerTimesheetsOrderingParam,
} from '../../clients/worker_timesheet_client';
import { showToast } from '../../config_and_helpers/helpers';
import {
  logListSearchEvent,
  logListSearchError,
} from '../../config_and_helpers/logger';
import { MinyanLogo } from '../../config_and_helpers/config';
import {
  abortListSearchController,
  getNextPageUrlFilterMismatch,
  isPartialListSearchQuery,
  isRunnableListSearchQuery,
  normalizeListSearchParam,
  resetPaginationForPartialSearch,
  INFINITE_SCROLL_THRESHOLD_PX,
} from '../../utils/listSearchRequest';
import '../../components/WorkerTimesheet/WorkerTimesheet.css';

const normalizeParam = normalizeListSearchParam;

/** Dropdown presets: value encodes `sortBy__sortDirection`. */
const SORT_OPTIONS = [
  { value: 'created_at__desc', label: 'הכי חדשים (לפי תאריך יצירה)' },
  { value: 'created_at__asc', label: 'הכי ישנים (לפי תאריך יצירה)' },
  { value: 'month__desc', label: 'חודש (חדש לישן)' },
  { value: 'month__asc', label: 'חודש (ישן לחדש)' },
  { value: 'id__desc', label: 'מזהה (גבוה לנמוך)' },
  { value: 'id__asc', label: 'מזהה (נמוך לגבוה)' },
];

function parseSortPreset(valueKey) {
  const [sortBy, sortDirection] = String(valueKey || '').split('__');
  if (!sortBy || !sortDirection) {
    return { sortBy: 'created_at', sortDirection: 'desc' };
  }
  return { sortBy, sortDirection };
}

function sortPresetFromState(sortBy, sortDirection) {
  return `${sortBy}__${sortDirection}`;
}

/** Subtitle for the worker timesheets list empty state (matches QuotesList pattern). */
function getWorkerTimesheetsEmptyMessage({ searchInput, monthFilter }) {
  if (searchInput.length >= 2) {
    return `לא נמצאו תוצאות עבור "${searchInput}"`;
  }
  if (monthFilter) {
    return 'אין דיווחים לחודש שנבחר';
  }
  return 'אין דיווחים זמינים';
}

const WorkerTimesheetsPage = () => {
  const { token } = useContext(AppContext);

  const [timesheets, setTimesheets] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const isMountedRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const currentRequestRef = useRef(null);
  const hasMoreRef = useRef(true);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const selectedSortOption =
    SORT_OPTIONS.find(
      o => o.value === sortPresetFromState(sortBy, sortDirection),
    ) || SORT_OPTIONS[0];

  /**
   * Fetches timesheets (first page or next). Cleans `next` URLs to avoid param drift.
   */
  const fetchTimesheets = useCallback(
    (
      {
        searchValue = '',
        nextPage = null,
        monthValue = '',
        sortByValue = 'created_at',
        sortDirectionValue = 'desc',
      } = {},
      newSearch = false,
    ) => {
      const trigger = newSearch ? 'search-or-filter' : 'scroll-or-refresh';
      const ordering = getWorkerTimesheetsOrderingParam(
        sortByValue,
        sortDirectionValue,
      );
      const params = {
        searchInput: searchValue,
        nextPage,
        monthFilter: monthValue,
        ordering,
        sortBy: sortByValue,
        sortDirection: sortDirectionValue,
      };

      if (!newSearch && !hasMoreRef.current && nextPage) {
        return;
      }

      if (!newSearch) {
        if (!nextPage || isLoading || isFetchingRef.current) {
          return;
        }
      }

      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }
      if (newSearch) {
        isFetchingRef.current = false;
      } else if (isFetchingRef.current) {
        return;
      }

      if (!hasMoreRef.current && nextPage) {
        return;
      }

      isFetchingRef.current = true;
      setIsLoading(true);
      if (newSearch) {
        setIsSearching(true);
        setTimesheets([]);
        setHasMore(true);
        hasMoreRef.current = true;
        setNextPageUrl(null);
      }

      const controller = new AbortController();
      currentRequestRef.current = controller;

      getWorkerTimesheets(token, setTimesheets, {
        searchInput: searchValue,
        nextPage,
        monthFilter: monthValue,
        sortBy: sortByValue,
        sortDirection: sortDirectionValue,
        signal: controller.signal,
      })
        .then(response => {
          if (!isMountedRef.current) return;
          /** Abort/superseded: another fetch replaced this controller — do not clear loading or mutate list. */
          if (currentRequestRef.current !== controller) {
            return;
          }

          if (response?.canceled) {
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            isFetchingRef.current = false;
            return;
          }

          const responseMeta = {
            success: response && response.success,
            reachedEnd: !!response?.reachedEnd,
            nextPage: response?.nextPage,
          };

          if (response && response.success) {
            let cleanNext = response.nextPage;
            if (cleanNext && !response.reachedEnd) {
              try {
                const url = new URL(cleanNext);
                const cleanUrl = new URL(url.origin + url.pathname);
                const pn = url.searchParams.get('page_num');
                if (pn) cleanUrl.searchParams.set('page_num', pn);
                if (searchValue) {
                  cleanUrl.searchParams.set('search', searchValue);
                }
                const m = normalizeParam(monthValue);
                if (/^\d{4}-(0[1-9]|1[0-2])$/.test(m)) {
                  cleanUrl.searchParams.set('month', m);
                }
                cleanUrl.searchParams.set('ordering', ordering);
                cleanNext = cleanUrl.toString();
              } catch (error) {
                logListSearchError(
                  'worker_timesheets',
                  'next-page-url-clean',
                  error,
                  { params },
                );
              }
            }

            setNextPageUrl(response.reachedEnd ? null : cleanNext);
            setHasMore(!response.reachedEnd);
            hasMoreRef.current = !response.reachedEnd;
          } else {
            setHasMore(false);
            hasMoreRef.current = false;
            setNextPageUrl(null);
            showToast(
              'לא ניתן לטעון את דיווחי העובדים. נסו שוב.',
              'error',
              'top-center',
              3000,
            );
          }

          logListSearchEvent(
            'worker_timesheets',
            response && response.success ? 'success' : 'failure',
            { trigger, params, responseMeta },
          );

          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
          isFetchingRef.current = false;
        })
        .catch(error => {
          if (
            error?.name === 'AbortError' ||
            error?.code === 'ERR_CANCELED'
          ) {
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            isFetchingRef.current = false;
            return;
          }

          if (!isMountedRef.current) return;

          logListSearchError('worker_timesheets', trigger, error, { params });

          setHasMore(false);
          hasMoreRef.current = false;
          setNextPageUrl(null);

          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
          isFetchingRef.current = false;
        });
    },
    [token, isLoading],
  );

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      abortListSearchController(currentRequestRef);
      isFetchingRef.current = false;
    };
  }, []);

  /** Initial load */
  useEffect(() => {
    if (!token) return;
    fetchTimesheets(
      {
        searchValue: '',
        nextPage: null,
        monthValue: monthFilter,
        sortByValue: sortBy,
        sortDirectionValue: sortDirection,
      },
      true,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only bootstrap
  }, [token]);

  const debouncedFetch = useCallback(
    (value, monthVal, sb, sd) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      if (value.length >= 2) {
        setTimesheets([]);
        setIsSearching(true);
        setHasMore(true);
        hasMoreRef.current = true;
        setNextPageUrl(null);
      }

      if (isRunnableListSearchQuery(value)) {
        debounceTimeoutRef.current = setTimeout(() => {
          fetchTimesheets(
            {
              searchValue: value,
              nextPage: null,
              monthValue: monthVal,
              sortByValue: sb,
              sortDirectionValue: sd,
            },
            true,
          );
        }, 300);
      } else if (isPartialListSearchQuery(value)) {
        setTimesheets([]);
        setIsSearching(false);
        resetPaginationForPartialSearch({
          setHasMore,
          hasMoreRef,
          setNextPageUrl,
          abortControllerRef: currentRequestRef,
        });
      }
    },
    [fetchTimesheets],
  );

  const handleSearchInput = value => {
    setSearchInput(value);
    debouncedFetch(value, monthFilter, sortBy, sortDirection);
  };

  const handleMonthChange = e => {
    const v = e.target.value || '';
    setMonthFilter(v);
    fetchTimesheets(
      {
        searchValue: searchInput,
        nextPage: null,
        monthValue: v,
        sortByValue: sortBy,
        sortDirectionValue: sortDirection,
      },
      true,
    );
  };

  const handleSortPresetChange = option => {
    if (!option) return;
    const key = option.value || 'created_at__desc';
    const { sortBy: sb, sortDirection: sd } = parseSortPreset(key);
    setSortBy(sb);
    setSortDirection(sd);
    fetchTimesheets(
      {
        searchValue: searchInput,
        nextPage: null,
        monthValue: monthFilter,
        sortByValue: sb,
        sortDirectionValue: sd,
      },
      true,
    );
  };

  const handleSortByChange = (field, direction) => {
    setSortBy(field);
    setSortDirection(direction);
    fetchTimesheets(
      {
        searchValue: searchInput,
        nextPage: null,
        monthValue: monthFilter,
        sortByValue: field,
        sortDirectionValue: direction,
      },
      true,
    );
  };

  const resetSearchValue = () => setSearchInput('');

  return (
    <>
      <header
        className="worker-ts-dashboard-top-banner text-center py-3 px-3"
        dir="rtl"
        lang="he"
        role="banner"
      >
        <div className="worker-ts-banner-content">
          <img
            src={MinyanLogo}
            alt="גן המניין לוגו"
            className="worker-ts-banner-logo"
          />
          <span className="fw-semibold fs-5 text-body">
            דיווח שעות - גן המניין
          </span>
        </div>
      </header>
      <div
        className="worker-ts-list-export-row d-flex justify-content-center py-2 px-3"
        dir="rtl"
        lang="he"
      >
        <Button
          variant="outline-success"
          size="sm"
          className="worker-ts-list-export-btn"
          onClick={() => setShowExportModal(true)}
          disabled={!token}
          aria-label="ייצוא דוח חודשי לאקסל"
        >
          <DownloadIcon fontSize="small" className="ms-1" />
          ייצוא דוח חודשי (אקסל)
        </Button>
      </div>
      <Container fluid className="py-3" dir="rtl" lang="he">
        <div className="d-flex justify-content-center mb-3 px-2">
          <div className="w-100" style={{ maxWidth: '56rem' }}>
            <Row className="g-2 align-items-end justify-content-center">
              <Col xs={12} md={5} lg={4}>
                <Form.Label className="small text-muted mb-1">חיפוש</Form.Label>
                <InputGroup className="worker-ts-search-input-group">
                  <InputGroup.Text className="worker-ts-search-addon">
                    <SearchIcon fontSize="small" />
                  </InputGroup.Text>
                  <Form.Control
                    type="search"
                    placeholder="שם או תעודת זהות"
                    value={searchInput}
                    onChange={e => handleSearchInput(e.target.value)}
                    aria-label="חיפוש דיווחי עובדים"
                    className="worker-ts-search-control"
                  />
                  <Button
                    variant="outline-secondary"
                    className="worker-ts-search-clear"
                    onClick={() => {
                      resetSearchValue();
                      fetchTimesheets(
                        {
                          searchValue: '',
                          nextPage: null,
                          monthValue: monthFilter,
                          sortByValue: sortBy,
                          sortDirectionValue: sortDirection,
                        },
                        true,
                      );
                    }}
                  >
                    ניקוי
                  </Button>
                </InputGroup>
              </Col>
              <Col xs={12} md={3} lg={3}>
                <Form.Label className="small text-muted mb-1">
                  סינון לפי חודש
                </Form.Label>
                <Form.Control type="month" value={monthFilter} onChange={handleMonthChange} />
              </Col>
              <Col xs={12} md={4} lg={4}>
                <div style={{ minWidth: '220px', maxWidth: '420px' }}>
                  <DropdownSelect
                    optionsList={SORT_OPTIONS}
                    label="מיון"
                    selectedValue={selectedSortOption}
                    setSelectedValue={opt => handleSortPresetChange(opt)}
                    disabledOptions={[]}
                  />
                </div>
              </Col>
            </Row>
          </div>
        </div>

      {(isLoading && !timesheets.length) || isSearching ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          {isSearching && searchInput.length >= 2 && (
            <div className="mt-2 text-muted">מחפש…</div>
          )}
        </div>
      ) : (
            <InfiniteScroll
              key={`${searchInput}-${monthFilter}-${sortBy}-${sortDirection}-${hasMore}`}
              pageStart={0}
              initialLoad={hasMoreRef.current}
              loadMore={() => {
                if (
                  isLoading ||
                  isFetchingRef.current ||
                  !hasMoreRef.current ||
                  !nextPageUrl ||
                  isPartialListSearchQuery(searchInput)
                ) {
                  return;
                }

                const ordering = getWorkerTimesheetsOrderingParam(
                  sortBy,
                  sortDirection,
                );
                const mismatch = getNextPageUrlFilterMismatch(nextPageUrl, {
                  search: searchInput,
                  month: monthFilter,
                  ordering,
                });
                if (mismatch === null) {
                  logListSearchError(
                    'worker_timesheets',
                    'next-page-url-stale-check',
                    new Error('Failed to parse nextPageUrl'),
                    {
                      nextPageUrl,
                      searchInput,
                      monthFilter,
                      sortBy,
                      sortDirection,
                    },
                  );
                  setNextPageUrl(null);
                  setHasMore(false);
                  hasMoreRef.current = false;
                  return;
                }
                if (mismatch.mismatched) {
                  logListSearchEvent(
                    'worker_timesheets',
                    'next-page-url-stale-mismatch',
                    {
                      nextPageUrl,
                      expected: mismatch.expected,
                      actual: mismatch.actual,
                    },
                  );
                  setNextPageUrl(null);
                  setHasMore(false);
                  hasMoreRef.current = false;
                  return;
                }

                fetchTimesheets(
                  {
                    searchValue: searchInput,
                    nextPage: nextPageUrl,
                    monthValue: monthFilter,
                    sortByValue: sortBy,
                    sortDirectionValue: sortDirection,
                  },
                  false,
                );
              }}
              hasMore={hasMore && !isLoading}
              loader={
                <div key="ts-loader" className="text-center py-2">
                  <Spinner animation="border" size="sm" role="status" />
                  <div className="mt-1">
                    <small className="text-muted">טוען עוד…</small>
                  </div>
                </div>
              }
              useWindow
              threshold={INFINITE_SCROLL_THRESHOLD_PX}
            >
              {!timesheets.length ? (
                <div className="text-center py-5">
                  <div className="mb-3">
                    <span
                      role="img"
                      aria-label="no results"
                      style={{ fontSize: '3rem' }}
                    >
                      📋
                    </span>
                  </div>
                  <h5 className="text-muted mb-2">לא נמצאו דיווחים</h5>
                  <p className="text-muted mb-3">
                    {getWorkerTimesheetsEmptyMessage({
                      searchInput,
                      monthFilter,
                    })}
                  </p>
                  {searchInput.length >= 2 && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setSearchInput('');
                        fetchTimesheets(
                          {
                            searchValue: '',
                            nextPage: null,
                            monthValue: monthFilter,
                            sortByValue: sortBy,
                            sortDirectionValue: sortDirection,
                          },
                          true,
                        );
                      }}
                    >
                      ניקוי חיפוש
                    </Button>
                  )}
                </div>
              ) : (
                <WorkerTimesheetsTable
                  timesheetList={timesheets}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSortByChange}
                  onDelete={() =>
                    fetchTimesheets(
                      {
                        searchValue: searchInput,
                        nextPage: null,
                        monthValue: monthFilter,
                        sortByValue: sortBy,
                        sortDirectionValue: sortDirection,
                      },
                      true,
                    )
                  }
                />
              )}
            </InfiniteScroll>
      )}
      </Container>

      <WorkerTimesheetMonthlyExportModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        token={token}
      />
    </>
  );
};

export default WorkerTimesheetsPage;
